import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faWrench, faCircleCheck, faTriangleExclamation, faArrowRight } from '@fortawesome/free-solid-svg-icons';

const API = process.env.REACT_APP_API_BASE_URL;
const BASE = `${API}/api/admin/Prices/rate-check`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const money = (n) => `$${Number(n).toFixed(Number(n) % 1 ? 2 : 0).replace(/\.?0+$/, (z) => (z.includes('.') ? '' : z))}`;
// the class time is saved as Lebanon time: show it as saved
const day = (v) => new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });

/* Prices & Offers › Check past classes: approved classes of a month whose teacher rate
   is not the teacher's rate for that course type (Standard / Mastery). Fix one, or all at once. */
const RateCheck = () => {
  const [month, setMonth] = useState(thisMonth);
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  const check = async () => {
    setBusy('check');
    setMsg(null);
    try {
      const res = await axios.get(`${BASE}?month=${month}`, auth());
      setList(res.data?.classes || []);
      if (res.data?.before) setMsg({ ok: true, text: res.data.message });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Couldn’t check.' });
    } finally {
      setBusy('');
    }
  };

  const fix = async (ids) => {
    if (ids.length > 1 && !window.confirm(`Change the teacher rate of ${ids.length} classes to the teacher’s rate for the course type?`)) return;
    setBusy(ids.length > 1 ? 'all' : `one-${ids[0]}`);
    setMsg(null);
    try {
      const res = await axios.post(`${BASE}/fix`, { ids }, auth());
      setList((l) => l.filter((c) => !ids.includes(c.id)));
      setMsg({ ok: true, text: `${res.data?.fixed || 0} ${res.data?.fixed === 1 ? 'class' : 'classes'} fixed. The teachers’ earnings update by themselves.` });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Couldn’t fix them.' });
    } finally {
      setBusy('');
    }
  };

  // money difference per teacher (half-paid classes count half)
  const byTeacher = {};
  (list || []).forEach((c) => {
    const k = c.teacher || 'Teacher';
    byTeacher[k] = (byTeacher[k] || 0) + (c.rate - c.saved) * (c.half ? 0.5 : 1);
  });

  return (
    <div className="rmcr-check">
      <h3>
        <FontAwesomeIcon icon={faMagnifyingGlass} /> Check past classes
      </h3>
      <p className="rmcr-sub">Approved classes not paid the teacher’s own Standard / Mastery rate. Check before paying the teachers — from November 2026 only (older classes keep the rates they were paid).</p>
      <div className="rmcr-check-bar">
        <input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setList(null); }} aria-label="Month" />
        <button type="button" className="rmcr-save" onClick={check} disabled={!month || !!busy}>
          {busy === 'check' ? 'Checking…' : 'Check'}
        </button>
        {list && list.length > 1 && (
          <button type="button" className="rmcr-fixall" onClick={() => fix(list.map((c) => c.id))} disabled={!!busy}>
            <FontAwesomeIcon icon={faWrench} /> {busy === 'all' ? 'Fixing…' : `Fix all ${list.length}`}
          </button>
        )}
      </div>

      {msg && (
        <p className={`rmcr-msg ${msg.ok ? '' : 'is-bad'}`} role="status">
          <FontAwesomeIcon icon={msg.ok ? faCircleCheck : faTriangleExclamation} /> {msg.text}
        </p>
      )}

      {list && list.length === 0 && !msg && (
        <p className="rmcr-msg">
          <FontAwesomeIcon icon={faCircleCheck} /> Every class of this month is paid the teacher’s rate.
        </p>
      )}

      {list && list.length > 0 && (
        <>
          <p className="rmcr-diff">
            {Object.entries(byTeacher).map(([t, d]) => (
              <span key={t}>
                {t}: <b className={d < 0 ? 'is-down' : 'is-up'}>{d > 0 ? '+' : '−'}{money(Math.abs(d))}</b>
              </span>
            ))}
          </p>
          <div className="rmcr-table-wrap">
            <table className="rmcr-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Teacher</th>
                  <th>Student</th>
                  <th>Course</th>
                  <th>Rate</th>
                  <th aria-label="Fix" />
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id}>
                    <td>{day(c.at)}</td>
                    <td>{c.teacher}</td>
                    <td>
                      <a href={`/admin/StudentProfile/${c.studentId}?tab=submitclasses`} target="_blank" rel="noopener noreferrer">
                        {c.student}
                      </a>
                    </td>
                    <td>
                      {c.course}
                      {c.type && <small> · {c.type === 'mastery' ? 'Mastery' : 'Standard'}</small>}
                      {c.project && <small>{c.project}</small>}
                    </td>
                    <td className="rmcr-rate">
                      <s>{money(c.saved)}</s> <FontAwesomeIcon icon={faArrowRight} /> <b>{money(c.rate)}</b>
                      {c.half && <small>half pay</small>}
                    </td>
                    <td>
                      <button type="button" className="rmcr-save" onClick={() => fix([c.id])} disabled={!!busy}>
                        {busy === `one-${c.id}` ? '…' : 'Fix'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default RateCheck;
